import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type DaySummary } from '../lib/api.ts';
import { useAuth } from '../App.tsx';
import { formatDuration, formatDecimalHours, todayInPlant } from '../../shared/time.ts';
import { policz } from '../../shared/pl.ts';
import { Blad, Kpi, Ladowanie, Pusto, Zmiana, type Ton } from '../ui/index.tsx';

/**
 * Analityka — efektywność w wybranym okresie, w podziale na brygady.
 *
 * Każda liczba jest tu zestawiona z poprzednim okresem tej samej długości.
 * Sama wartość niewiele mówi: 82% jest dobre, jeśli miesiąc temu było 74%,
 * i złe, jeśli było 91%. Bez tego porównania raport co miesiąc zaczyna dyskusję
 * od zera.
 */

interface Grupa { kod: string; nazwa: string; kolejnosc: number }
interface Suma {
  creditMin: number; availableMin: number; registeredMin: number;
  gapMin: number; efficiency: number | null; days: number;
}
interface Wiersz {
  employee: { id: number; login: string; zone: string | null; grupa?: Grupa };
  total: Suma;
  brakWpisow: boolean;
  days: DaySummary[];
}
interface Raport {
  from: string; to: string; zakres: string;
  employees: Wiersz[];
  overall: Suma;
  bezWpisow: number;
}

const proc = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);
const klasa = (v: number | null) =>
  v === null ? 'text-plum-muted' : v >= 0.9 ? 'text-ok' : v >= 0.7 ? 'text-warn' : 'text-bad';
const ton = (v: number | null): Ton => (v === null ? 'neutral' : v >= 0.9 ? 'ok' : v >= 0.7 ? 'warn' : 'bad');

export default function Reports() {
  const { user } = useAuth();
  const isManager = user?.role !== 'worker';
  const nav = useNavigate();

  const [to, setTo] = useState(todayInPlant());
  const [from, setFrom] = useState(() => przesun(todayInPlant(), -6));
  const [scopeAll, setScopeAll] = useState(isManager);
  const [wszyscy, setWszyscy] = useState(isManager);
  const [d, setD] = useState<Raport | null>(null);
  const [poprzedni, setPoprzedni] = useState<Raport | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [laduje, setLaduje] = useState(false);
  const [rozwiniety, setRozwiniety] = useState<number | null>(null);

  const dni = Math.max(1, roznicaDni(from, to) + 1);
  // Poprzedni okres tej samej długości, kończący się dzień przed bieżącym.
  const poprzedniDo = przesun(from, -1);
  const poprzedniOd = przesun(poprzedniDo, -(dni - 1));

  const pobierz = (od: string, doD: string) => {
    const p = new URLSearchParams({ from: od, to: doD });
    if (scopeAll) p.set('scope', 'all');
    if (scopeAll && wszyscy) p.set('wszyscy', '1');
    return api.get<Raport>(`/reports/efficiency?${p}`);
  };

  const load = () => {
    setLaduje(true);
    setBlad(null);
    Promise.all([pobierz(from, to), pobierz(poprzedniOd, poprzedniDo).catch(() => null)])
      .then(([teraz, wczesniej]) => { setD(teraz); setPoprzedni(wczesniej); })
      .catch((e) => setBlad(e.message))
      .finally(() => setLaduje(false));
  };
  useEffect(load, [from, to, scopeAll, wszyscy]);

  const zWpisami = d?.employees.filter((e) => !e.brakWpisow) ?? [];
  const bezWpisow = d?.employees.filter((e) => e.brakWpisow) ?? [];

  // Grupujemy brygadami — chłodnicy, elektrycy, testy… Serwer wyprowadza
  // przynależność z tego, gdzie człowiek faktycznie pracował w tym okresie.
  const brygady: Array<{ grupa: Grupa; ludzie: Wiersz[] }> = [];
  for (const w of zWpisami) {
    const g = w.employee.grupa ?? { kod: 'BRAK', nazwa: 'bez przypisania', kolejnosc: 99 };
    const istnieje = brygady.find((b) => b.grupa.kod === g.kod);
    if (istnieje) istnieje.ludzie.push(w);
    else brygady.push({ grupa: g, ludzie: [w] });
  }
  const suma = (ludzie: Wiersz[], pole: 'creditMin' | 'availableMin') =>
    ludzie.reduce((sa, x) => sa + x.total[pole], 0);
  const efGrupy = (ludzie: Wiersz[]) => {
    const dost = suma(ludzie, 'availableMin');
    return dost > 0 ? suma(ludzie, 'creditMin') / dost : null;
  };

  // Ta sama brygada w poprzednim okresie — do porównania nagłówka grupy.
  const efGrupyWczesniej = (kod: string) => {
    const ludzie = (poprzedni?.employees ?? []).filter(
      (w) => !w.brakWpisow && (w.employee.grupa?.kod ?? 'BRAK') === kod,
    );
    return ludzie.length ? efGrupy(ludzie) : null;
  };

  const ustawZakres = (odDni: number) => {
    const t = todayInPlant();
    setTo(t);
    setFrom(przesun(t, -(odDni - 1)));
  };

  if (blad) return <Blad tresc={blad} ponow={load} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-head-sm">Analityka</h1>
        <span className="text-body-sm text-plum-muted">
          {from} – {to} · {policz(dni, 'dzien')}
        </span>
        <span className="ml-auto text-caption text-plum-muted">
          poprzedni okres: {poprzedniOd} – {poprzedniDo}
        </span>
      </div>

      <div className="card p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label" htmlFor="from">Od</label>
          <input id="from" type="date" className="field !w-auto tnum" value={from}
                 onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="to">Do</label>
          <input id="to" type="date" className="field !w-auto tnum" value={to} max={todayInPlant()}
                 onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="flex gap-1">
          <button className="subtab" onClick={() => ustawZakres(7)}>7 dni</button>
          <button className="subtab" onClick={() => ustawZakres(30)}>30 dni</button>
          <button className="subtab" onClick={() => ustawZakres(90)}>90 dni</button>
        </div>
        {isManager && (
          <>
            <label className="tap inline-flex items-center gap-2 text-body-sm text-plum-soft
                              min-h-[40px] px-3 border border-line-strong rounded-lg bg-white cursor-pointer">
              <input type="checkbox" checked={scopeAll} onChange={(e) => setScopeAll(e.target.checked)} />
              Cały zakład
            </label>
            <label className={`tap inline-flex items-center gap-2 text-body-sm text-plum-soft
                               min-h-[40px] px-3 border border-line-strong rounded-lg bg-white cursor-pointer ${
                                 scopeAll ? '' : 'opacity-40 pointer-events-none'}`}>
              <input type="checkbox" checked={wszyscy} onChange={(e) => setWszyscy(e.target.checked)} />
              Także bez wpisów
            </label>
          </>
        )}
        <div className="ml-auto flex gap-2">
          <button className="btn-ghost" onClick={() => przesunOkno(setFrom, setTo, -dni)}>◀ wstecz</button>
          <button className="btn-ghost" onClick={() => przesunOkno(setFrom, setTo, dni)}>dalej ▶</button>
        </div>
      </div>

      {laduje && !d && <Ladowanie tekst="Liczenie…" />}

      {d && (
        <>
          <section className="card p-5">
            <h2 className="text-label uppercase text-plum-muted mb-4">Okres wobec poprzedniego</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              <Kpi
                etykieta="Efektywność"
                wartosc={proc(d.overall.efficiency)}
                ton={ton(d.overall.efficiency)}
                udzial={d.overall.efficiency ?? 0}
                pod={<Zmiana teraz={d.overall.efficiency} wczesniej={poprzedni?.overall.efficiency ?? null} procent />}
                onClick={() => nav(`/praca?od=${from}&do=${to}`)}
              />
              <Kpi
                etykieta="Wypracowana norma"
                wartosc={formatDuration(d.overall.creditMin)}
                pod={<>
                  {formatDecimalHours(d.overall.creditMin)} ·{' '}
                  <Zmiana teraz={d.overall.creditMin / 60} wczesniej={poprzedni ? poprzedni.overall.creditMin / 60 : null} />
                  {' h'}
                </>}
                onClick={() => nav(`/praca?od=${from}&do=${to}`)}
              />
              <Kpi
                etykieta="Czas dostępny"
                wartosc={formatDuration(d.overall.availableMin)}
                pod={`${formatDecimalHours(d.overall.availableMin)} · ${policz(d.overall.days, 'zmiana')}`}
              />
              <Kpi
                etykieta="Nierozpisany czas"
                wartosc={formatDuration(Math.max(0, d.overall.gapMin))}
                ton={d.overall.gapMin > 60 ? 'bad' : 'neutral'}
                pod={d.overall.gapMin > 0 ? 'do uzupełnienia' : 'wszystko rozpisane'}
                onClick={() => nav(`/praca?od=${from}&do=${to}`)}
              />
            </div>
          </section>

          <section className="card overflow-hidden">
            <div className="overflow-x-auto max-h-[70vh]">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Pracownik</th>
                    <th className="text-right">Dni</th>
                    <th className="text-right">Norma</th>
                    <th className="text-right">Dostępne</th>
                    <th className="text-right">Luka</th>
                    <th className="text-right">Efektywność</th>
                    <th className="text-right">Zmiana</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {zWpisami.length === 0 && (
                    <tr><td colSpan={8}><Pusto tytul="Brak danych w tym okresie"
                                               opis="Zmień zakres dat albo odznacz filtr lokalizacji." /></td></tr>
                  )}
                  {brygady.map((b) => {
                    const ef = efGrupy(b.ludzie);
                    return (
                      <Fragment key={b.grupa.kod}>
                        <tr className="bg-canvas">
                          <td colSpan={5} className="!py-2">
                            <span className="text-label uppercase text-plum">{b.grupa.nazwa}</span>
                            <span className="ml-2 text-caption text-plum-muted">
                              {policz(b.ludzie.length, 'pracownik')}
                            </span>
                          </td>
                          <td className={`!py-2 text-right tnum font-semibold ${klasa(ef)}`}>{proc(ef)}</td>
                          <td className="!py-2 text-right text-caption">
                            <Zmiana teraz={ef} wczesniej={efGrupyWczesniej(b.grupa.kod)} procent />
                          </td>
                          <td className="!py-2"></td>
                        </tr>

                        {b.ludzie.map((row) => {
                          const wcz = poprzedni?.employees.find((x) => x.employee.id === row.employee.id);
                          const otwarty = rozwiniety === row.employee.id;
                          return (
                            <Fragment key={row.employee.id}>
                              <tr
                                data-klik="1"
                                tabIndex={0}
                                aria-expanded={otwarty}
                                onClick={() => setRozwiniety(otwarty ? null : row.employee.id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setRozwiniety(otwarty ? null : row.employee.id);
                                  }
                                }}
                              >
                                <td>
                                  <span className="font-semibold tnum text-plum">{row.employee.login}</span>
                                  {row.employee.zone && (
                                    <span className="ml-2 text-caption text-plum-muted">{row.employee.zone}</span>
                                  )}
                                </td>
                                <td className="text-right tnum">{row.total.days}</td>
                                <td className="text-right tnum">{formatDuration(row.total.creditMin)}</td>
                                <td className="text-right tnum">{formatDuration(row.total.availableMin)}</td>
                                <td className={`text-right tnum ${row.total.gapMin > 60 ? 'text-bad' : 'text-plum-muted'}`}>
                                  {formatDuration(Math.max(0, row.total.gapMin))}
                                </td>
                                <td className={`text-right tnum font-semibold ${klasa(row.total.efficiency)}`}>
                                  {proc(row.total.efficiency)}
                                </td>
                                <td className="text-right text-caption">
                                  <Zmiana teraz={row.total.efficiency} wczesniej={wcz?.total.efficiency ?? null} procent />
                                </td>
                                <td className="text-right">
                                  <button className="btn-quiet !min-h-0 !py-1 !px-2 text-caption"
                                          onClick={(e) => { e.stopPropagation(); nav(`/pracownicy/${row.employee.id}?od=${from}&do=${to}`); }}>
                                    Karta →
                                  </button>
                                </td>
                              </tr>

                              {otwarty && (
                                <tr className="bg-canvas">
                                  <td colSpan={8}>
                                    <table className="w-full text-caption">
                                      <thead className="text-plum-muted">
                                        <tr className="text-left">
                                          <th className="py-1 font-semibold">Dzień</th>
                                          <th className="py-1 font-semibold text-right">Rozpisane</th>
                                          <th className="py-1 font-semibold text-right">Dostępne</th>
                                          <th className="py-1 font-semibold text-right">Norma</th>
                                          <th className="py-1 font-semibold text-right">Luka</th>
                                          <th className="py-1 font-semibold text-right">Efektywność</th>
                                          <th></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {row.days.map((dz) => (
                                          <tr key={dz.work_date} className="border-t border-line">
                                            <td className="py-1.5 tnum">{dz.work_date}</td>
                                            <td className="py-1.5 text-right tnum">{formatDuration(dz.registeredMin)}</td>
                                            <td className="py-1.5 text-right tnum">{formatDuration(dz.availableMin)}</td>
                                            <td className="py-1.5 text-right tnum">{formatDuration(dz.creditMin)}</td>
                                            <td className={`py-1.5 text-right tnum ${dz.gapMin > 15 ? 'text-bad' : 'text-plum-muted'}`}>
                                              {formatDuration(Math.max(0, dz.gapMin))}
                                            </td>
                                            <td className={`py-1.5 text-right tnum font-semibold ${klasa(dz.efficiency)}`}>
                                              {proc(dz.efficiency)}
                                            </td>
                                            <td className="py-1.5 text-right">
                                              <button className="btn-quiet !min-h-0 !py-0.5 !px-2 text-caption"
                                                      onClick={() => nav(`/praca?od=${dz.work_date}&do=${dz.work_date}&pracownik=${row.employee.id}`)}>
                                                Wpisy →
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {bezWpisow.length > 0 && (
            <section className="card overflow-hidden">
              <div className="px-5 py-4 border-b border-line">
                <h3 className="text-sub">Bez wpisów w tym okresie — {policz(bezWpisow.length, 'pracownik')}</h3>
                <p className="text-body-sm text-plum-soft mt-1.5">
                  Celowo <b>nie</b> pokazuję im 0% efektywności. Brak wpisów najczęściej znaczy urlop,
                  zwolnienie albo delegację, a tego aplikacja jeszcze nie wie — zero byłoby oskarżeniem
                  bez dowodu. Po spięciu z aplikacją obecności rozróżnimy nieobecność od niezarejestrowanej pracy.
                </p>
              </div>
              <div className="p-5 flex flex-wrap gap-2">
                {bezWpisow.map((row) => (
                  <button key={row.employee.id} className="badge-neutral tnum hover:border-line-strong"
                          onClick={() => nav(`/pracownicy/${row.employee.id}?od=${from}&do=${to}`)}>
                    {row.employee.login}
                  </button>
                ))}
              </div>
            </section>
          )}

          <p className="text-caption text-plum-muted">
            Efektywność = wypracowana norma / czas dostępny (nominał 7,33 h + nadgodziny minus dodatkowa przerwa 15 min).
            Gdy nad jedną sztuką pracowało kilka osób, norma dzieli się proporcjonalnie do wkładu czasowego.
            Zestawienie obejmuje wyłącznie załogę A4; S8 ma osobne narzędzie.
            Przynależność do brygady wynika z tego, gdzie pracownik faktycznie pracował w wybranym okresie,
            a nie z ręcznie ustawianego pola. Kolumna „Zmiana" porównuje z okresem
            {' '}{poprzedniOd} – {poprzedniDo}. Kliknij wiersz, żeby rozwinąć dni.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- daty

function przesun(data: string, dni: number): string {
  const d = new Date(`${data}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dni);
  return d.toISOString().slice(0, 10);
}

function roznicaDni(od: string, doD: string): number {
  return Math.round(
    (Date.parse(`${doD}T00:00:00Z`) - Date.parse(`${od}T00:00:00Z`)) / 86_400_000,
  );
}

type SetDate = React.Dispatch<React.SetStateAction<string>>;

function przesunOkno(setFrom: SetDate, setTo: SetDate, dni: number) {
  setFrom((prev) => przesun(prev, dni));
  setTo((prev) => przesun(prev, dni));
}
